import { useCallback, useEffect } from 'react';

// Extend Window interface to include WT
declare global {
  interface Window {
    WT?: {
      optimizeModule?: {
        prototype?: {
          wtConfigObj?: {
            data?: Record<string, any>;
          };
        };
      };
      refresh?: () => void;
      optimize?: {
        setup: (config: any) => void;
      };
      persistWTFlag?: (key: string, value: any) => void;
      setWTDataAndPersist?: (key: string, value: any) => void;
      hydrateWTData?: () => void;
      retrieveFunnelData?: () => any;
    };
  }
}

// Key under which we persist flags in sessionStorage
const PERSIST_KEY = 'WTDataLayer';

/**
 * Hook for managing WT (Webtrends) optimization module data
 * with sessionStorage persistence so flags survive reloads.
 */
export const useWTODataLayer = () => {
  // Read persisted flags from sessionStorage
  const readPersisted = (): Record<string, any> => {
    try {
      return JSON.parse(sessionStorage.getItem(PERSIST_KEY) || '{}');
    } catch {
      return {};
    }
  };

  // Write flags map back to sessionStorage
  const writePersisted = (data: Record<string, any>) => {
    sessionStorage.setItem(PERSIST_KEY, JSON.stringify(data));
  };

  /**
   * Sets a value in the WT optimization module data object
   * and persists it to sessionStorage
   */
  const setWTData = useCallback((key: string, value: any = true) => {
    if (
      typeof window !== 'undefined' &&
      window.WT?.optimizeModule?.prototype?.wtConfigObj?.data
    ) {
      window.WT.optimizeModule.prototype.wtConfigObj.data[key] = value;

      // persist
      const all = readPersisted();
      all[key] = value;
      writePersisted(all);
    }
  }, []);

  /**
   * Gets a value from the WT optimization module data object
   */
  const getWTData = useCallback(
    (key: string) =>
      window.WT?.optimizeModule?.prototype?.wtConfigObj?.data?.[key] ??
      undefined,
    []
  );

  /**
   * Checks if the WT optimization module is available
   */
  const isWTAvailable = useCallback(
    () => Boolean(window.WT?.optimizeModule?.prototype?.wtConfigObj?.data),
    []
  );

  /**
   * Removes a specific key from the WT optimization module data object
   * and updates sessionStorage
   */
  const removeWTData = useCallback((key: string) => {
    if (
      typeof window !== 'undefined' &&
      window.WT?.optimizeModule?.prototype?.wtConfigObj?.data
    ) {
      delete window.WT.optimizeModule.prototype.wtConfigObj.data[key];

      // update persisted
      const all = readPersisted();
      delete all[key];
      writePersisted(all);
    }
  }, []);

  /**
   * Clears all data from the WT optimization module data object
   * and clears sessionStorage
   */
  const clearAllWTData = useCallback(() => {
    if (
      typeof window !== 'undefined' &&
      window.WT?.optimizeModule?.prototype?.wtConfigObj
    ) {
      window.WT.optimizeModule.prototype.wtConfigObj.data = {};
      sessionStorage.removeItem(PERSIST_KEY);
    }
  }, []);

  /**
   * Resets specific tracking flags to false
   */
  const resetWTFlags = useCallback(
    (keys: string[]) => {
      keys.forEach((key) => setWTData(key, false));
    },
    [setWTData]
  );

  // On mount, hydrate the persisted flags back into the WT data layer
  useEffect(() => {
    const all = readPersisted();
    Object.entries(all).forEach(([key, value]) => {
      if (window.WT?.optimizeModule?.prototype?.wtConfigObj?.data) {
        window.WT.optimizeModule.prototype.wtConfigObj.data[key] = value;
      }
    });
  }, []);

  return {
    setWTData,
    getWTData,
    isWTAvailable,
    removeWTData,
    clearAllWTData,
    resetWTFlags,
  };
};

export default useWTODataLayer;
